/*
 *
 * LabelTemplateDetail
 *
 */

import React from "react"
import { connect } from "react-redux"
import {
	delayedDispatch,
	setBreadcrumb,
	setLoader,
	setTitle,
	updateCrumb,
} from "store/actions"
import {
	Button,
	Col,
	Collapse,
	Input,
	Modal,
	notification,
	Row,
	Switch,
} from "antd"
import { Helmet } from "react-helmet"
import { ContentWrapper } from "components"
import AceEditor from "react-ace"
import { API, Endpoints } from "utils/api"
import Strings from "utils/strings"
import { generateLabelTemplate } from "utils/labelAI"
import { diffLines } from "diff"
import "ace-builds/src-noconflict/mode-html"
import "ace-builds/src-noconflict/theme-monokai"
import "ace-builds/src-min-noconflict/ext-language_tools"
import "ace-builds/src-min-noconflict/ext-searchbox"
import "./styles.scss"

class LabelTemplateDetail extends React.Component<any, any> {
	aceEditorRef: any = null

	constructor(props: any) {
		super(props)

		this.state = {
			labelTemplate: null,
			availableVariables: [],
			hasUnsavedFields: false,
			aiPrompt: "",
			aiLoading: false,
			aiDiff: null,
		}

		this.handleSave = this.handleSave.bind(this)
	}

	async componentDidMount() {
		const { dispatch, match } = this.props
		const isNew = match?.params?.id === "new"

		dispatch(setTitle(""))

		if (!isNew) {
			await this.getData()
		} else {
			this.setState({
				labelTemplate: { name: "", template: "", active: true },
			})
		}

		delayedDispatch(
			setBreadcrumb(() => {
				const { hasUnsavedFields, labelTemplate } = this.state
				const isEditing = this.props.match?.params?.id !== "new"

				return {
					locations: [
						{
							text: Strings.sidebar.settings,
							route: "/settings",
							icon: "preferences",
						},
						{
							text: Strings.settings.labelTemplates,
							route: "/settings/label-templates",
							icon: "tag",
						},
						{
							text: isEditing
								? labelTemplate?.name || "..."
								: Strings.businesses.labelNew,
							icon: "tag",
						},
					],
					actions: [
						{
							type: "button",
							text: Strings.generic.save,
							onClick: this.handleSave,
							disabled: !hasUnsavedFields,
							className: hasUnsavedFields ? "BreadcrumbButtonSuccess" : "",
							isSave: true,
						},
					],
				}
			})
		)
	}

	componentDidUpdate() {
		const { dispatch } = this.props
		dispatch(updateCrumb())
	}

	async getData() {
		const { dispatch, match } = this.props

		dispatch(setLoader(true))

		try {
			const response = await API.get({
				url: Endpoints.uriLabelTemplates(match?.params?.id),
			})

			if (response.ok) {
				const { labelTemplates: labelTemplate, availableVariables = [] } =
					response.data.results || {}
				this.setState({ labelTemplate, availableVariables })
			} else {
				notification.error({
					message: Strings.settings.labelTemplates,
					description:
						response?.data?.message || Strings.serverErrors.wentWrong,
					placement: "bottomRight",
					duration: 5,
				})
			}
		} catch (err) {
			notification.error({
				message: Strings.settings.labelTemplates,
				description: Strings.serverErrors.wentWrong,
				placement: "bottomRight",
				duration: 5,
			})
		}

		dispatch(setLoader(false))
	}

	async handleSave() {
		const { labelTemplate } = this.state
		const { dispatch, match, user } = this.props
		const isNew = match?.params?.id === "new"

		if (!labelTemplate?.name?.trim()) {
			return notification.error({
				message: Strings.settings.labelTemplates,
				description: Strings.businesses.labelNameRequired,
				placement: "bottomRight",
				duration: 5,
			})
		}

		if (!labelTemplate?.template?.trim()) {
			return notification.error({
				message: Strings.settings.labelTemplates,
				description: Strings.businesses.labelTemplateRequired,
				placement: "bottomRight",
				duration: 5,
			})
		}

		dispatch(setLoader(true))

		try {
			const request = isNew ? API.post : API.put
			const response = await request({
				url: Endpoints.uriLabelTemplates(isNew ? "" : match?.params?.id),
				data: {
					name: labelTemplate.name,
					template: labelTemplate.template,
					active: labelTemplate.active ?? true,
					lastModifiedBy: user?._id,
				},
			})

			if (response.ok) {
				this.setState({ hasUnsavedFields: false })

				notification.success({
					message: Strings.settings.labelTemplates,
					description: isNew
						? Strings.settings.labelTemplateAdded
						: Strings.settings.labelTemplateEdited,
					placement: "bottomRight",
					duration: 5,
				})
			} else {
				notification.error({
					message: Strings.settings.labelTemplates,
					description:
						response?.data?.message || Strings.serverErrors.wentWrong,
					placement: "bottomRight",
					duration: 5,
				})
			}
		} catch (err) {
			notification.error({
				message: Strings.settings.labelTemplates,
				description: Strings.serverErrors.wentWrong,
				placement: "bottomRight",
				duration: 5,
			})
		}

		dispatch(setLoader(false))
	}

	extractTemplateVariables(template: string): string[] {
		const matches = template.match(/\{\{(\w+)\}\}/g) || []
		return [...new Set(matches.map((m: string) => m.slice(2, -2)))]
	}

	insertVariable(variable: string) {
		const editor = this.aceEditorRef?.editor
		if (editor) {
			editor.session.insert(editor.getCursorPosition(), `{{${variable}}}`)
			editor.focus()
		}
	}

	async generateWithAI() {
		const { labelTemplate, aiPrompt, availableVariables = [] } = this.state

		if (!aiPrompt?.trim()) return

		this.setState({ aiLoading: true })

		try {
			const newTemplate = await generateLabelTemplate(
				aiPrompt,
				labelTemplate?.template || "",
				availableVariables
			)

			this.setState({
				aiDiff: { before: labelTemplate?.template || "", after: newTemplate },
				aiPrompt: "",
			})
		} catch (err) {
			notification.error({
				message: Strings.settings.labelTemplates,
				description: Strings.serverErrors.wentWrong,
				placement: "bottomRight",
				duration: 5,
			})
		}

		this.setState({ aiLoading: false })
	}

	renderAIDiff() {
		const { aiDiff } = this.state
		const chunks = aiDiff ? diffLines(aiDiff.before, aiDiff.after) : []

		return (
			<Modal
				visible={!!aiDiff}
				title={Strings.businesses.labelAIAssistant}
				width="80vw"
				centered
				onCancel={() => this.setState({ aiDiff: null })}
				footer={
					<div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
						<Button onClick={() => this.setState({ aiDiff: null })}>
							{Strings.generic.cancel}
						</Button>
						<Button
							type="primary"
							onClick={() => {
								this.setState((prev: any) => ({
									labelTemplate: {
										...prev.labelTemplate,
										template: prev.aiDiff.after,
									},
									aiDiff: null,
									hasUnsavedFields: true,
								}))
							}}
						>
							{Strings.generic.confirm}
						</Button>
					</div>
				}
			>
				<div className="LabelAIDiffUnified">
					{chunks.map((chunk, chunkIdx) => {
						const type = chunk.added
							? "added"
							: chunk.removed
								? "removed"
								: "unchanged"
						const symbol = chunk.added ? "+" : chunk.removed ? "-" : " "
						const lines = chunk.value.split("\n")
						if (lines[lines.length - 1] === "") lines.pop()
						return lines.map((line, lineIdx) => (
							<div
								key={`${chunkIdx}-${lineIdx}`}
								className={`LabelAIDiffLine --${type}`}
							>
								<span className="LabelAIDiffLineSymbol">{symbol}</span>
								<pre className="LabelAIDiffLineContent">{line}</pre>
							</div>
						))
					})}
				</div>
			</Modal>
		)
	}

	render() {
		const {
			labelTemplate,
			availableVariables = [],
			aiPrompt,
			aiLoading,
		} = this.state
		const usedVariables = this.extractTemplateVariables(
			labelTemplate?.template ?? ""
		)

		return (
			<React.Fragment>
				<ContentWrapper>
					<Helmet>
						<title>
							{labelTemplate?.name || Strings.settings.labelTemplates}
						</title>
						<meta
							name="description"
							content={Strings.settings.labelTemplatesDescription}
						/>
					</Helmet>
					<Row gutter={[12, 16]}>
						<Col xs={24}>
							<Row gutter={[16, 0]} align="middle">
								<Col flex="1">
									<label
										htmlFor="label_name"
										className="InputLabel --label-required"
									>
										{Strings.fields.name}
									</label>
									<Input
										id="label_name"
										placeholder={Strings.fields.name}
										value={labelTemplate?.name || ""}
										onChange={(e: any) => {
											const value = e.target.value
											this.setState((prev: any) => ({
												labelTemplate: { ...prev.labelTemplate, name: value },
												hasUnsavedFields: true,
											}))
										}}
									/>
								</Col>
								<Col>
									<label className="InputLabel">
										{Strings.businesses.labelActive}
									</label>
									<div style={{ marginTop: 4 }}>
										<Switch
											checked={labelTemplate?.active ?? true}
											onChange={(val: boolean) =>
												this.setState((prev: any) => ({
													labelTemplate: { ...prev.labelTemplate, active: val },
													hasUnsavedFields: true,
												}))
											}
										/>
									</div>
								</Col>
							</Row>
						</Col>

						{availableVariables.length > 0 && (
							<Col xs={24}>
								<Collapse ghost className="LabelTemplateCollapse">
									<Collapse.Panel
										header={Strings.businesses.labelAvailableVars}
										key="vars"
									>
										<div className="LabelTemplateVarChips">
											{availableVariables.map((variable: string) => {
												const isUsed = usedVariables.includes(variable)
												return (
													<button
														key={variable}
														className={`LabelTemplateVarChip${isUsed ? " --used" : ""}`}
														onClick={() => this.insertVariable(variable)}
													>
														{`{{${variable}}}`}
													</button>
												)
											})}
										</div>
										{usedVariables.some(
											(v: string) => !availableVariables.includes(v)
										) && (
											<div className="LabelTemplateInvalidVars">
												<span className="LabelTemplateInvalidLabel">
													{Strings.businesses.labelInvalidVars}:
												</span>
												<div className="LabelTemplateVarChips">
													{usedVariables
														.filter(
															(v: string) => !availableVariables.includes(v)
														)
														.map((variable: string) => (
															<span
																key={variable}
																className="LabelTemplateVarChip --invalid"
															>
																{`{{${variable}}}`}
															</span>
														))}
												</div>
											</div>
										)}
									</Collapse.Panel>
								</Collapse>
							</Col>
						)}

						<Col xs={24}>
							<div className="LabelAISection">
								<div className="LabelAISectionHeader">
									<span className="LabelAISectionLabel">
										{Strings.businesses.labelAIAssistant}
									</span>
								</div>
								<div className="LabelAIInputRow">
									<Input.TextArea
										className="LabelAIInput"
										placeholder={Strings.businesses.labelAIPlaceholder}
										value={aiPrompt}
										onChange={(e: any) =>
											this.setState({ aiPrompt: e.target.value })
										}
										autoSize={{ minRows: 2, maxRows: 4 }}
										onKeyDown={(e: React.KeyboardEvent) => {
											if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
												this.generateWithAI()
											}
										}}
									/>
									<button
										className="LabelAIBtn"
										onClick={() => this.generateWithAI()}
										disabled={!aiPrompt?.trim() || aiLoading}
									>
										{aiLoading ? (
											<span className="LabelAIBtnSpinner" />
										) : (
											<span className="LabelAIBtnIcon">✦</span>
										)}
										{aiLoading
											? Strings.businesses.labelAIGenerating
											: Strings.businesses.labelAIGenerate}
									</button>
								</div>
							</div>
						</Col>

						<Col xs={24} xl={12}>
							<span
								className="InputLabel --label-required"
								style={{ height: 40, display: "flex", alignItems: "center" }}
							>
								HTML
							</span>
							<AceEditor
								ref={(ref: any) => {
									this.aceEditorRef = ref
								}}
								mode="html"
								theme="monokai"
								value={labelTemplate?.template ?? ""}
								onChange={(val: string) =>
									this.setState((prev: any) => ({
										labelTemplate: { ...prev.labelTemplate, template: val },
										hasUnsavedFields: true,
									}))
								}
								fontSize={14}
								showPrintMargin={false}
								showGutter
								highlightActiveLine
								width="100%"
								setOptions={{
									enableBasicAutocompletion: true,
									enableLiveAutocompletion: true,
									enableSnippets: false,
									showLineNumbers: true,
									tabSize: 2,
									useWorker: false,
								}}
							/>
						</Col>

						<Col xs={24} xl={12}>
							<span
								className="InputLabel"
								style={{ height: 40, display: "flex", alignItems: "center" }}
							>
								{Strings.businesses.labelPreview}
							</span>
							<div className="LabelDetailPreview">
								<iframe
									srcDoc={labelTemplate?.template || ""}
									title="Label Preview"
								/>
							</div>
						</Col>
					</Row>
				</ContentWrapper>
				{this.renderAIDiff()}
			</React.Fragment>
		)
	}
}

const mapStateToProps = (state: any) => ({
	user: state.user,
})

export default connect(mapStateToProps)(LabelTemplateDetail)
